import { Assert } from "./Assert";
import { Nullable, PartialNullable } from "./Extensions";
import { IPromisePublic, PromiseHelper } from "./Helpers/PromiseHelper";
import { $$, JNativeStatic } from "./JNative";
import { LinqArray } from "./LinqArray";
import { NAMESPACE } from "./_Namespace";
import { PropertyChange } from "./PropertyChange";
import { IRadObject, Property, PropertyObject, RadObject, RadObjectPropertyValues } from "./RadObject";
import { WebApiClient } from "./Server/WebApiClient";
import { ServiceManager } from "./ServiceManager";
import { ApplicationDataService } from "./Ui/ApplicationDataService";

export type PermissionCachePropertyValues =
    RadObjectPropertyValues
    & {
        data: object;
        engines: { [name: string]: boolean };
        permissions: any[];
        propertyPermissions: any[];
        teams: any[];
        webApiClient: WebApiClient;
    };

export type PermissionCacheProperties = {
    [name in keyof PermissionCachePropertyValues]: Property<PermissionCachePropertyValues[name]>
};

export interface IPermissionCache extends IRadObject, PermissionCacheProperties
{
}

export class PermissionCache extends RadObject<PermissionCachePropertyValues> implements IPermissionCache
{
    public static override classNamespace = NAMESPACE + "PermissionCache";
    public static override className = "PermissionCache";

    // #region Static

    // #region Global Variables

    public static REFRESH_INTERVAL_IN_MINUTES = 30;

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PermissionCachePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                permissions: [],
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _loadPermissionsAsyncPromise?: IPromisePublic<any>;
    protected _loadingPermissionsTimeoutId: number;

    // #endregion

    // #region Properties

    public data!: PropertyObject;
    public engines!: Property<{ [name: string]: boolean }>;
    public permissions!: Property<any[]>;
    public propertyPermissions!: Property<any[]>;
    public teams!: Property<any[]>;
    public webApiClient!: Property<WebApiClient>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PermissionCachePropertyValues>)
    {
        super(propertyValues);

        if (!this.webApiClient.get())
            throw new Error("A webApiClient is required.");

        this._loadingPermissionsTimeoutId = 0;
    }

    // #endregion

    // #region Public Methods

    public clear()
    {
        this._clearUserMenu();

        this.data.set(null);
        this.permissions.set(null);
        this.propertyPermissions.set(null);
        this.teams.set(null);

        $$(document).trigger("SeriouslyRAD.PermissionCache:PermissionsChanged", self);
    }
    public hasPermissionForEntity(entityName: Nullable<string | undefined>, permissionLevel: Nullable<number | undefined>)
    {
        const
            permissions = this.permissions.get();

        if (!permissions)
            return false;

        if (!permissionLevel)
            return false;

        return permissions
            .some(item => item.entityName === entityName && (item.permission & permissionLevel) > 0);
    }
    public hasPermissionForEntityProperty(entityName: string, entityPropertyName: string, permissionLevel: number)
    {
        const
            propertyPermissions = this.propertyPermissions.get();

        if (!propertyPermissions)
            return false;

        const
            propertyPermission =
                LinqArray.create(propertyPermissions)
                    .firstOrDefault(item => item.entityName.toLowerCase() === entityName.toLowerCase() && item.entityPropertyName.toLowerCase() === entityPropertyName.toLowerCase());

        if (!propertyPermission)
            return true; // Default true

        return (propertyPermission.permission & permissionLevel) > 0;
    }
    public isInTeam(teamName: string)
    {
        const
            teams = this.teams.get();

        if (!teams)
            return false;

        return teams.some(item => item.teamName === teamName);
    }
    public refresh()
    {
        return this._loadPermissionsAsync();
    }

    // #endregion

    // #region Protected Methods

    protected _clearUserMenu()
    {
        const
            userMenu = $$(".rad-action-user-menu-open").radControl();

        if (userMenu)
        {
            userMenu
                .$$element.get()
                ?.find(".rad-text")
                .empty();
        }
    }
    protected _dataChanged(change: PropertyChange<object>)
    {
        /* Intentionally empty */
    }
    protected _enginesChanged(change: PropertyChange<object>)
    {
        /* Intentionally empty */
    }
    protected _loadPermissionsAsync()
    {
        if (this._loadPermissionsAsyncPromise)
            return this._loadPermissionsAsyncPromise;

        this._loadPermissionsAsyncPromise = PromiseHelper.createPromisePublic();

        if (this._loadingPermissionsTimeoutId !== 0)
        {
            clearTimeout(this._loadingPermissionsTimeoutId);
            this._loadingPermissionsTimeoutId = 0;
        }

        const
            webApiClient = this.webApiClient.get();

        Assert.isNotNullOrUndefined(webApiClient, "webApiClient");

        webApiClient
            .createApiMethodRequest("Security", "GetSecurityInformation", null, null, false, true)
            .executeAsync()
            .then(data =>
            {
                if (!data)
                    return;

                ServiceManager.getRequired(ApplicationDataService).authenticated.set(true);

                this.data.set(data);

                this._clearUserMenu();

                const
                    userMenu = $$(".rad-action-user-menu-open").radControl();

                if (userMenu && data.name)
                {
                    const
                        $$text = userMenu.$$element.get()?.find(".rad-text"),
                        nameDetails = [{
                            attributes: {
                                "class": "name"
                            },
                            nodeName: "span",
                            text: data.name
                        }];

                    if (data.tenantName)
                    {
                        nameDetails.push({
                            attributes: {
                                "class": "tenant"
                            },
                            nodeName: "span",
                            text: data.tenantName
                        });
                    }

                    const
                        $$userInformation = JNativeStatic.create$$(nameDetails);

                    $$text.append($$userInformation);
                }

                this.engines.set(data.engines);
                this.permissions.set(data.permissions);
                this.propertyPermissions.set(data.propertyPermissions);
                this.teams.set(data.teams);

                $$(document).trigger("SeriouslyRAD.PermissionCache:PermissionsChanged", self);

                this._loadPermissionsAsyncPromise?.resolve(data);
            })
            .catch(error =>
            {
                this._loadPermissionsAsyncPromise?.reject(error);
            })
            .finally(() =>
            {
                this._loadingPermissionsTimeoutId =
                    window.setTimeout(() => { this.refresh(); }, PermissionCache.REFRESH_INTERVAL_IN_MINUTES * 60 * 1000);
                delete this._loadPermissionsAsyncPromise;
            });

        return this._loadPermissionsAsyncPromise;
    }
    protected _permissionsChanged(change: PropertyChange<any[]>)
    {
        /* Intentionally empty */
    }
    protected _propertyPermissionsChanged(change: PropertyChange<any[]>)
    {
        /* Intentionally empty */
    }
    protected _teamsChanged(change: PropertyChange<any[]>)
    {
        /* Intentionally empty */
    }
    protected _webApiClientChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PermissionCachePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.data = new PropertyObject(this, propertyValues.data, this._dataChanged);
        this.engines = new Property<{ [name: string]: boolean }>(this, "System.Object, System.Private.CoreLib", propertyValues.engines, this._enginesChanged);
        this.permissions = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.permissions, this._permissionsChanged);
        this.propertyPermissions = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.propertyPermissions, this._propertyPermissionsChanged);
        this.teams = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.teams, this._teamsChanged);
        this.webApiClient = new PropertyObject(this, propertyValues.webApiClient, this._webApiClientChanged);
    }

    // #endregion

    // #endregion
}

